import api, { authService } from './api';

const dedupeVendorUsers = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = [
      String(item?.username || '').trim().toLowerCase(),
      String(item?.email || '').trim().toLowerCase(),
    ].join('|');
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const userService = {
  // Search users_twz for vendor widget autocomplete
  searchVendorUsers: async (query, vendor) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/user/vendor-users', {
        params: { q: query, vendor, limit: 10 },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: dedupeVendorUsers(response.data.items || []),
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to search users',
      };
    }
  },

  // Save vendor login info for current authenticated user
  saveVendorLoginInfo: async (vendor, loginInfo, userId, extras = {}) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post(
        '/user/vendor-login',
        {
          vendor,
          login_info: loginInfo,
          location: extras.location || null,
          card_id: extras.cardId || null,
          user_id: userId || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to save vendor login info',
      };
    }
  },

  // Lookup inventory snapshot for selected user/vendor
  lookupInventory: async (userId, vendor) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/inventory/lookup', {
        params: {
          user_id: userId,
          vendor,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to lookup inventory',
      };
    }
  },

  lookupEmptyAccounts: async (vendor, location) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/user/empty-accounts', {
        params: {
          vendor,
          location,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        items: response.data.items || [],
        totalCount: response.data.total_count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to lookup empty accounts',
      };
    }
  },

  // Get all users (admin only)
  getUsers: async () => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get('/admin/users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        users: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch users',
      };
    }
  },

  // Create new user (admin only)
  createUser: async (userData) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/admin/users', userData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        user: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create user',
      };
    }
  },

  // Delete user (admin only)
  deleteUser: async (userId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      await api.delete(`/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: 'User deleted successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete user',
      };
    }
  },

  // Update user role (admin only)
  updateUserRole: async (userId, newRole) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/admin/users/${userId}/role`, newRole, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update user role',
      };
    }
  },

  // Update user via FastAPI-Users endpoint (for basic user info)
  updateUser: async (userId, userData) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/auth/users/${userId}`, userData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        user: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update user',
      };
    }
  },

  // Get user permissions
  getUserPermissions: async (userId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.get(`/permissions/users/${userId}/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        permissions: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch user permissions',
      };
    }
  },

  // Create table permission
  createTablePermission: async (permissionData) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.post('/permissions/table-permissions', permissionData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        permission: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create table permission',
      };
    }
  },

  // Update table permission
  updateTablePermission: async (permissionId, permissionData) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      const response = await api.patch(`/permissions/table-permissions/${permissionId}`, permissionData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        permission: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update table permission',
      };
    }
  },

  // Delete table permission
  deleteTablePermission: async (permissionId) => {
    try {
      const token = authService.getToken();
      if (!token) throw new Error('No authentication token');

      await api.delete(`/permissions/table-permissions/${permissionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        message: 'Table permission deleted successfully',
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete table permission',
      };
    }
  },
};
